import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserStore } from '../lib/userStore';
import { useLeagueStore } from '../lib/leagueStore';
import { isTslhTenant, getTenantAwarePath } from '../lib/tenantStore';
import { supabase } from '../lib/supabase';
import { TSLH_LEAGUE_ID, forceTslhLogout } from '../lib/tenantSession';
import { hasAcceptedLeagueRules } from '../lib/leagueRulesAcceptance';

/** Restore TSLH before mounting pages or competing navigation guards. */
export function TslhRestoreGate({ children }: { children: ReactNode }) {
  const userId = useUserStore(s => s.user_id);
  const username = useUserStore(s => s.username);
  const leagueId = useLeagueStore(s => s.selectedLeague?.id);
  const navigate = useNavigate();
  const location = useLocation();
  const [readyUser, setReadyUser] = useState<number | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const active = isTslhTenant() && !!username && !!userId && !location.pathname.startsWith('/ci-dashboard');
  const ready = readyUser === userId && leagueId === TSLH_LEAGUE_ID;

  useEffect(() => {
    if (!active) { setReadyUser(null); return; }
    if (ready) return;
    let cancelled = false;
    setError(false);
    const timeout = window.setTimeout(() => {
      cancelled = true;
      setError(true);
    }, 15000);
    const restore = async () => {
      try {
        const { data: membership, error: memberError } = await supabase
          .from('leagues_users').select('initial_build_complete')
          .eq('league_id', TSLH_LEAGUE_ID).eq('user_id', userId).maybeSingle();
        if (cancelled) return;
        if (memberError) throw memberError;
        if (!membership) {
          forceTslhLogout(navigate, 'Ce compte ne fait pas partie du pool TSLH. Reconnecte-toi.');
          return;
        }
        const { data: league, error: leagueError } = await supabase
          .from('leagues').select('name').eq('league_id', TSLH_LEAGUE_ID).single();
        if (cancelled) return;
        if (leagueError || !league) throw leagueError || new Error('Missing league');
        useLeagueStore.getState().setSelectedLeague({ id: TSLH_LEAGUE_ID, name: league.name });
        const user = useUserStore.getState();
        user.setCurrentLeagueId(TSLH_LEAGUE_ID);
        user.setBpmSession(false);
        user.setTslhSession(true);
        const destination = membership.initial_build_complete
          ? '/my-office'
          : hasAcceptedLeagueRules(TSLH_LEAGUE_ID) ? '/build-my-team' : '/league-rules-onboarding';
        navigate(getTenantAwarePath(destination), { replace: true });
        setReadyUser(userId);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        window.clearTimeout(timeout);
      }
    };
    void restore();
    return () => { cancelled = true; window.clearTimeout(timeout); };
  }, [active, userId, ready, attempt, navigate]);

  if (!active || ready) return <>{children}</>;
  return (
    <div className="embed-content-wrapper p-4 text-center" aria-live="polite">
      {error ? <>
        <p role="alert">Impossible de charger votre pool pour le moment.</p>
        <button className="btn btn-primary" onClick={() => setAttempt(value => value + 1)}>Réessayer</button>
      </> : <div role="status">
        <span className="spinner-border" aria-hidden="true" />
        <p>Chargement de votre pool…</p>
      </div>}
    </div>
  );
}
